import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PROFILE_VERTICALS,
  PROFILE_VERTICAL_CONFIGS,
  getProfileVerticalFromAppContext,
  type ProfileVertical,
} from '@photofeeler/shared';
import { useUserStore } from '@/stores/user';
import { useGameStore } from '@/stores/game';

interface NewTabProps {
  initialVertical?: ProfileVertical;
}

export function NewTab({ initialVertical }: NewTabProps) {
  const navigate = useNavigate();
  const { credits } = useUserStore();
  const { primaryContext } = useGameStore();
  const defaultVertical = getProfileVerticalFromAppContext(primaryContext) || 'dating';
  const [selectedVertical, setSelectedVertical] = useState<ProfileVertical>(initialVertical || defaultVertical);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialVertical) {
      setSelectedVertical(initialVertical);
    }
  }, [initialVertical]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      navigate('/upload', { state: { file, preselectedVertical: selectedVertical } });
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  return (
    <div className="pb-24 pt-4 px-5 max-w-lg mx-auto animate-fade-in">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />

      <h1 className="font-sans text-2xl text-ink mb-1 text-center" style={{ fontWeight: 600 }}>
        Rate a photo
      </h1>
      <p className="text-sm text-ink-faint font-sans mb-6 text-center" style={{ fontWeight: 300 }}>
        Get an AI score on your photo in seconds.
      </p>

      {/* 2-card action grid */}
      <div className="grid grid-cols-2 gap-3 mb-8">
        <button
          onClick={openFilePicker}
          className="card-hover p-6 text-center border-2 border-rose/20 bg-rose-faint"
        >
          <div className="text-3xl mb-2">&#x1F4F7;</div>
          <div className="text-sm font-sans font-medium text-rose mb-0.5">Select a photo</div>
          <div className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
            Pick from your library
          </div>
        </button>
        <button
          onClick={() => navigate(`/compare?vertical=${selectedVertical}`)}
          className="card-hover p-6 text-center"
        >
          <div className="text-3xl mb-2">&#x1F50D;</div>
          <div className="text-sm font-sans font-medium text-ink mb-0.5">Compare multiple photos</div>
          <div className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
            Find your best one
          </div>
        </button>
      </div>

      {/* Context Selector */}
      <div className="mb-6">
        <h2 className="text-sm font-sans font-medium text-ink-muted mb-3 text-center">
          What&apos;s this for?
        </h2>
        <div className="flex flex-wrap justify-center gap-2">
          {PROFILE_VERTICALS.map(vertical => {
            const config = PROFILE_VERTICAL_CONFIGS[vertical];
            return (
              <button
                key={config.id}
                onClick={() => setSelectedVertical(config.id)}
                className={`px-4 py-2 rounded-full text-xs font-sans font-medium transition-all duration-200 ${
                  selectedVertical === config.id
                    ? 'bg-rose text-white shadow-warm'
                    : 'bg-surface border border-surface-border text-ink-muted hover:border-ink/10'
                }`}
              >
                {config.emoji} {config.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Credits */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-surface rounded-full border border-surface-border">
          <span className="text-xs font-sans text-ink-faint" style={{ fontWeight: 300 }}>
            Credits remaining:
          </span>
          <span className="text-xs font-sans font-medium text-ink">{credits}</span>
        </div>
        {credits === 0 && (
          <button
            onClick={() => navigate('/purchase')}
            className="block mx-auto mt-3 text-xs font-sans font-medium text-rose hover:text-rose-dark transition-colors"
          >
            Buy more credits
          </button>
        )}
      </div>
    </div>
  );
}
